import { Fragment, useState } from "react";
import { ago, api, can, Drawer, Empty, Loading, Pill, setQuery, useLoad, useMe, useRoute } from "./lib";
import { Planner } from "./planner";

export const GROUP_NAME: Record<string, string> = {
  people: "People", acq: "Acquisition", serving: "AI & hosting", software: "Software & tools",
  events: "Events & travel", ops: "Operations", pro: "Professional services", other: "Other",
};
export const usd = (v: number | null | undefined) =>
  v == null ? "Unknown" : (v < -0.5 ? "−" : "") + "$" + Math.round(Math.abs(v)).toLocaleString("en-US");
const monthLabel = (k: string) => new Date(k + "-15T12:00:00Z").toLocaleDateString("en-US", { month: "short", year: "2-digit", timeZone: "UTC" });

export function Finance() {
  const { me } = useMe();
  const route = useRoute();
  const { data, error } = useLoad<any>("/api/finance");
  const tab = route.q.get("tab") || "plan";
  if (!data) return <div className="page"><Loading error={error} /></div>;
  if (!data.import)
    return (
      <div className="page">
        <div className="banner warn">The budget hasn't been imported yet. An admin runs <code>bun scripts/import-budget.ts</code> (see README).</div>
      </div>
    );
  const rec = data.import.reconciliation;
  const scen = route.q.get("scenario") || data.scenarios.find((s: any) => s.key === "base")?.id || data.scenarios[0]?.id;
  return (
    <div className="page">
      <div className={`banner ${data.import.status === "reconciled" ? "good" : "bad"}`}>
        <div>
          <strong>{data.import.status === "reconciled" ? "Imported and reconciled" : "Import has differences"}</strong> from the {data.import.source_name}, {ago(data.import.imported_at)}.{" "}
          {rec.checks.toLocaleString()} checks against the original page's own formulas, {rec.discrepancies.length} differences. Uses the model's {rec.used}, run as of {data.import.as_of}, starting from the {data.import.start_cash_source.toLowerCase()}.
        </div>
      </div>
      <div className="row between">
        <div className="tabs">
          <button className={tab === "planner" ? "on" : ""} onClick={() => setQuery({ tab: "planner" })}>Scenario planner</button>
          <button className={tab === "plan" ? "on" : ""} onClick={() => setQuery({ tab: null })}>Budget vs actual</button>
          <button className={tab === "spending" ? "on" : ""} onClick={() => setQuery({ tab: "spending" })}>Spending records</button>
          <button className={tab === "scenarios" ? "on" : ""} onClick={() => setQuery({ tab: "scenarios" })}>Scenarios</button>
        </div>
        {tab === "plan" && (
          <select className="input" style={{ width: "auto" }} value={scen} onChange={(e) => setQuery({ scenario: e.target.value })} aria-label="Scenario">
            {data.scenarios.map((s: any) => <option key={s.id} value={s.id}>{s.name} scenario</option>)}
          </select>
        )}
      </div>
      {tab === "planner" ? <Planner /> : tab === "spending" ? <Spending /> : tab === "scenarios" ? <Scenarios data={data} /> : <Plan id={scen} />}
      <section className="card">
        <h2>Revenue</h2>
        <div className="banner info" style={{ marginTop: 8 }}>{data.revenue_actual.note}</div>
        {!can(me, "finance.edit") && <p className="muted small">You can view finance but not edit it.</p>}
      </section>
    </div>
  );
}

function Plan({ id }: { id: string }) {
  const { data, error } = useLoad<any>(`/api/finance/scenarios/${id}`, [id]);
  const [open, setOpen] = useState<string | null>(null);
  if (!data) return <Loading error={error} />;
  const groups = Object.keys(GROUP_NAME);
  const ym = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date()).slice(0, 7);
  return (
    <>
      <p className="muted small" style={{ margin: 0 }}>{data.story}</p>
      <section className="card" style={{ padding: 0 }}>
        <div className="scroll-x">
          <table className="t" style={{ fontVariantNumeric: "tabular-nums" }}>
            <thead>
              <tr><th>Month</th><th style={{ textAlign: "right" }}>Planned</th><th style={{ textAlign: "right" }}>Committed</th><th style={{ textAlign: "right" }}>Actual</th><th style={{ textAlign: "right" }} title="Cleared card spend and bills paid through Brex. Not payroll or anything paid outside Brex.">Spent through Brex</th><th style={{ textAlign: "right" }}>Variance</th><th className="hide-sm" style={{ textAlign: "right" }}>Plan: cash in</th><th className="hide-sm" style={{ textAlign: "right" }}>Plan: month-end cash</th></tr>
            </thead>
            <tbody>
              {data.months.map((m: any) => (
                <Fragment key={m.month}>
                  <tr className="clickable" onClick={() => setOpen(open === m.month ? null : m.month)} style={m.month === ym ? { background: "var(--accent-soft)" } : undefined}>
                    <td><strong>{monthLabel(m.month)}</strong></td>
                    <td style={{ textAlign: "right" }}>{usd(m.planned.total)}</td>
                    <td style={{ textAlign: "right" }}>{m.committed ? usd(m.committed.total) : <span className="muted">None recorded</span>}</td>
                    <td style={{ textAlign: "right" }}>{m.actual ? usd(m.actual.total) : <span className="muted">Unknown</span>}</td>
                    <td style={{ textAlign: "right" }}>{m.brex ? usd(m.brex.total) : <span className="muted">—</span>}</td>
                    <td style={{ textAlign: "right", color: m.variance > 0 ? "var(--bad)" : m.variance < 0 ? "var(--good)" : undefined }}>{m.variance == null ? <span className="muted">—</span> : (m.variance > 0 ? "+" : "") + usd(m.variance)}</td>
                    <td className="hide-sm" style={{ textAlign: "right" }}>{usd(m.plan_revenue_cash)}</td>
                    <td className="hide-sm" style={{ textAlign: "right", color: m.plan_end_cash < 0 ? "var(--bad)" : undefined }}>{usd(m.plan_end_cash)}</td>
                  </tr>
                  {open === m.month &&
                    groups.map((g) => (
                      <tr key={m.month + g} className="small">
                        <td style={{ paddingLeft: 24 }}>{GROUP_NAME[g]}</td>
                        <td style={{ textAlign: "right" }}>{usd(m.planned.by_group[g])}</td>
                        <td style={{ textAlign: "right" }}>{m.committed ? usd(m.committed.by_group[g]) : ""}</td>
                        <td style={{ textAlign: "right" }}>{m.actual ? usd(m.actual.by_group[g]) : ""}</td>
                        <td /><td /><td className="hide-sm" /><td className="hide-sm" />
                      </tr>
                    ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <p className="muted tiny" style={{ margin: 0 }}>Planned comes from the imported model. Committed and actual come from spending records. "Unknown" means nothing has been recorded for that month, which isn't the same as $0. "Spent through Brex" is cleared card spend and bills paid in Brex, read by the morning agent; it leaves out payroll and anything paid outside Brex, so it isn't the month's full actual. Click a month for categories.</p>
    </>
  );
}

function Scenarios({ data }: { data: any }) {
  return (
    <div className="grid">
      {data.scenarios.map((s: any) => (
        <section key={s.id} className="card stack">
          <h2>{s.name}</h2>
          <p className="sub" style={{ margin: 0 }}>{s.story}</p>
          <div className="form-2 small">
            <div><div className="muted tiny">Lowest cash (next {s.stats.H} months)</div>{usd(s.stats.lowest)}</div>
            <div><div className="muted tiny">New money needed to stay above the floor</div>{usd(s.stats.need)}</div>
            <div><div className="muted tiny">Operating break-even</div>{s.stats.breakEven ?? "Not within the model"}</div>
            <div><div className="muted tiny">Paying users at the end (model)</div>{Math.round(s.stats.payingEnd).toLocaleString()}</div>
            <div><div className="muted tiny">BairesDev paid / left (model)</div>{usd(s.stats.bdPaid)} / {usd(s.stats.bdLeft)}</div>
          </div>
          <p className="muted tiny" style={{ margin: 0 }}>All model figures are planning assumptions, not results.</p>
        </section>
      ))}
      <section className="card stack">
        <h2>Reconciliation by category</h2>
        <p className="sub" style={{ margin: 0 }}>Operating costs over the whole plan, as imported. Each matched the original to the cent.</p>
        <div className="scroll-x">
          <table className="t small">
            <thead><tr><th>Category</th>{data.import.reconciliation.scenarios.map((s: any) => <th key={s.key} style={{ textAlign: "right" }}>{s.name}</th>)}</tr></thead>
            <tbody>
              {Object.keys(data.import.reconciliation.scenarios[0].by_group).map((g) => (
                <tr key={g}><td>{g}</td>{data.import.reconciliation.scenarios.map((s: any) => <td key={s.key} style={{ textAlign: "right" }}>{usd(s.by_group[g])}</td>)}</tr>
              ))}
              <tr><td><strong>Total</strong></td>{data.import.reconciliation.scenarios.map((s: any) => <td key={s.key} style={{ textAlign: "right" }}><strong>{usd(s.opex_total)}</strong></td>)}</tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function Spending() {
  const { me, toast } = useMe();
  const { data, reload } = useLoad<any[]>("/api/finance/expenses");
  const campaigns = useLoad<any[]>("/api/campaigns").data || [];
  const orgs = useLoad<any[]>(can(me, "partners.view") ? "/api/orgs" : null).data || [];
  const [adding, setAdding] = useState(false);
  const ym = new Intl.DateTimeFormat("en-CA", { timeZone: "America/Chicago" }).format(new Date()).slice(0, 7);
  return (
    <>
      <div className="row between">
        <p className="muted small" style={{ margin: 0 }}>Brex isn't connected to the portal yet, so actual spending is entered by hand for now.</p>
        {can(me, "finance.edit") && <button className="btn primary" onClick={() => setAdding(true)}>Record spending</button>}
      </div>
      <section className="card" style={{ padding: 0 }}>
        {!data ? <div style={{ padding: 20 }}><Loading /></div> : data.length ? (
          <div className="scroll-x">
            <table className="t">
              <thead><tr><th>Month</th><th>Vendor</th><th className="hide-sm">Category</th><th>Status</th><th style={{ textAlign: "right" }}>Amount</th><th className="hide-sm">For</th></tr></thead>
              <tbody>
                {data.map((e) => (
                  <tr key={e.id}>
                    <td>{monthLabel(e.month)}{e.recurring ? <div className="muted tiny">Monthly from here</div> : null}</td>
                    <td>{e.vendor}<div className="muted tiny">{e.description}</div></td>
                    <td className="hide-sm">{GROUP_NAME[e.grp]}</td>
                    <td><Pill tone={e.status === "actual" ? "good" : e.status === "committed" ? "accent" : ""}>{e.status[0].toUpperCase() + e.status.slice(1)}</Pill></td>
                    <td style={{ textAlign: "right" }}>{usd(e.amount)}</td>
                    <td className="hide-sm small">{e.links.map((l: any) => l.label).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div style={{ padding: 20 }}><Empty>No spending recorded yet.</Empty></div>}
      </section>
      {adding && (
        <Drawer title="Record spending" onClose={() => setAdding(false)}>
          <form
            className="form"
            onSubmit={async (ev) => {
              ev.preventDefault();
              const fd = new FormData(ev.currentTarget);
              const links = [fd.get("campaign") && { type: "campaign", id: fd.get("campaign") }, fd.get("org") && { type: "organization", id: fd.get("org") }].filter(Boolean);
              try {
                await api("/api/finance/expenses", {
                  method: "POST",
                  body: { month: fd.get("month"), vendor: fd.get("vendor"), description: fd.get("description"), grp: fd.get("grp"), amount: fd.get("amount"), status: fd.get("status"), recurring: fd.get("recurring") || null, links },
                });
                toast("Saved");
                setAdding(false);
                reload();
              } catch (err: any) {
                toast(err.message, true);
              }
            }}
          >
            <div className="form-2">
              <label className="field">Status
                <select className="input" name="status" defaultValue="actual">
                  <option value="actual">Actual (paid)</option><option value="committed">Committed (agreed or invoiced)</option><option value="planned">Planned</option>
                </select>
              </label>
              <label className="field">Counts toward<input className="input" name="month" type="month" defaultValue={ym} required /></label>
              <label className="field">Amount (USD)<input className="input" name="amount" type="number" step="0.01" required /></label>
              <label className="field">Category
                <select className="input" name="grp">{Object.entries(GROUP_NAME).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              </label>
            </div>
            <label className="field">Vendor or payee<input className="input" name="vendor" required /></label>
            <label className="field">Note<input className="input" name="description" /></label>
            <label className="field">Repeats
              <select className="input" name="recurring"><option value="">No</option><option value="monthly">Monthly (planned or committed only)</option></select>
            </label>
            <div className="form-2">
              <label className="field">Campaign<select className="input" name="campaign"><option value="">None</option>{campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></label>
              {orgs.length > 0 && <label className="field">Partner<select className="input" name="org"><option value="">None</option>{orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</select></label>}
            </div>
            <div className="row"><button className="btn primary">Save</button></div>
          </form>
        </Drawer>
      )}
    </>
  );
}
